import React, { useEffect } from 'react';
import analyticsService from '~/src/services/analytics';

import {
  useGetClioQuestionnaireById,
  useGetClioQuestionnaireUrl,
  useGetQuestionnaireTemplates,
  usePrepopulateQuestionnaireResponses,
  useUpdateQuestionnaireSubmission,
} from '~/src/entities/questionnaires/hooks';
import { ShareLink } from './ShareLink';
import { SelectMatter } from './SelectMatter';
import { useCurrentOrgFprint } from '~/src/entities/user';
import { DialogContainer } from '~/src/components/Modal/mui';
import { MessageDialogError, MessageDialogLoading } from '../shared';

const ShareQuestionnaireLink: React.FC<{
  questionnaireId: string;
  page: 'questionnaire_builder_page' | 'questionnaire_list_page';
  totalQuestions: number;
  onClose: () => void;
}> = ({ questionnaireId, page, totalQuestions, onClose }) => {
  const orgFprint = useCurrentOrgFprint();
  const {
    data: shareData,
    mutateAsync: shareLink,
    isError,
    isLoading,
  } = useGetClioQuestionnaireUrl();
  const {
    setMatterId: setMatterIdForPrepopulate,
    submissionId,
    setSubmissionId,
    isPrepopulatingLoading,
    isPrepopulatingDone,
  } = usePrepopulateQuestionnaireResponses(orgFprint);
  const {
    mutateAsync: updateSubmissionResponse,
    isLoading: isSubmissionLoading,
    isError: isSubmissionError,
  } = useUpdateQuestionnaireSubmission(orgFprint);
  const {
    data: questionnaire,
    isLoading: isQuestionnaireLoading,
    isError: isQuestionnaireError,
  } = useGetClioQuestionnaireById(orgFprint, questionnaireId);

  const { data: questionnaireTemplates } = useGetQuestionnaireTemplates(
    orgFprint,
    questionnaireId,
  );
  const deprecatedTemplates = (questionnaireTemplates ?? [])
    .filter((t) => t.template.is_source_deprecated)
    .map((t) => ({
      title: t.template.title as string | null,
      subtitle: t.template.subtitle,
    }));

  const [matterId, setMatterId] = React.useState<null | string>(null);
  const [submissionTitle, setSubmissionTitle] = React.useState<string | null>(
    null,
  );
  const [initialSubmissionTitle, setInitialSubmissionTitle] = React.useState<
    string | null
  >(null);

  useEffect(() => {
    if (submissionTitle && initialSubmissionTitle === null) {
      setInitialSubmissionTitle(submissionTitle);
    }
  }, [submissionTitle, initialSubmissionTitle]);

  const handleGenerateLink = async (matterId: string) => {
    if (!submissionTitle) return;

    setMatterId(matterId);
    const analytics =
      initialSubmissionTitle !== submissionTitle
        ? [
            {
              name: 'Renamed Submission',
              data: {
                old_name: initialSubmissionTitle,
                new_name: submissionTitle,
              },
            },
          ]
        : [];
    const res = await shareLink({
      orgFprint,
      questionnaireId,
      matterId,
      analytics,
    });
    updateSubmissionResponse({
      submissionId: res.submission_id!,
      submissionUpdate: { title: submissionTitle },
    });
  };

  useEffect(() => {
    if (!shareData || !matterId) return;

    setMatterIdForPrepopulate(matterId);
    setSubmissionId(shareData.submission_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, shareData]);

  if (isError || isSubmissionError || isQuestionnaireError)
    return <MessageDialogError onClose={onClose} />;

  if (
    !questionnaire ||
    (matterId &&
      (isLoading ||
        isPrepopulatingLoading ||
        !isPrepopulatingDone ||
        isSubmissionLoading ||
        isQuestionnaireLoading))
  )
    return <MessageDialogLoading onClose={onClose} />;

  if (shareData && matterId) {
    const questionnaireLink = shareData.link;

    analyticsService.track('Clicked on Generate Share Link', {
      questionnaireId,
      matterId,
      page: 'questionnaire_list_page',
    });

    return (
      <ShareLink
        questionnaireLink={questionnaireLink}
        onClose={onClose}
        questionnaireId={questionnaireId}
        matterId={matterId}
        submissionId={submissionId!}
        submissionTitle={submissionTitle!}
        page={page}
      />
    );
  }

  const isShareable = totalQuestions > 0;

  return (
    <SelectMatter
      onClose={onClose}
      onGenerateLink={handleGenerateLink}
      questionnaireId={questionnaireId}
      isShareable={isShareable}
      page={page}
      questionnaireTitle={questionnaire.title}
      setSubmissionTitle={setSubmissionTitle}
      deprecatedTemplates={deprecatedTemplates}
    />
  );
};

export const ShareQuestionnaireLinkModal: React.FC<{
  questionnaireId: string;
  page: 'questionnaire_builder_page' | 'questionnaire_list_page';
  open: boolean;
  totalQuestions: number;
  onClose: () => void;
}> = ({ questionnaireId, page, open, totalQuestions, onClose }) => (
  <DialogContainer open={open} onClose={onClose}>
    <ShareQuestionnaireLink
      page={page}
      questionnaireId={questionnaireId}
      onClose={onClose}
      totalQuestions={totalQuestions}
    />
  </DialogContainer>
);
